import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { logger } from "@/services/LoggingService";
import type { AssetType, NormalizedAsset } from "@/types/assets";
import { fetchAssetsFor } from "@/components/portfolio/asset-dialog/utils/asset-search";
import { getRecordCount } from "@/components/portfolio/asset-dialog/utils/record-count";
import { fetchAllocatedAssetIdSet } from "@/components/portfolio/asset-dialog/utils/current-allocations";
import { mapFundTypeToAssetType } from "@/components/portfolio/asset-dialog/utils/map-fund-type";
import { performAllocationInsert } from "@/components/portfolio/asset-dialog/utils/allocation-insert";
import { useAddAssetState } from "@/hooks/asset-add/useAddAssetState";
import { useAddAssetData } from "@/hooks/asset-add/useAddAssetData";

// Asset types and normalized asset interface now imported from '@/types/assets'

export function useEnhancedAddAsset(portfolioId: string, onAssetAdded: () => void, externalOpen?: boolean, externalSetOpen?: (open: boolean) => void, defaultAssetType?: string) {
  // Debug logging for asset type mapping
  const mappedAssetType = mapFundTypeToAssetType(defaultAssetType);
  logger.debug('useEnhancedAddAsset', 'Asset type mapping', { defaultAssetType, mappedAssetType });
  
  // Use externally provided open state if available, otherwise maintain internal state
  const [internalOpen, setInternalOpen] = useState(false);
  const open = externalOpen !== undefined ? externalOpen : internalOpen;
  const [assets, setAssets] = useState<NormalizedAsset[]>([]);
  const {
    selectedAssetType,
    equitySource,
    searchQuery,
    setSelectedAssetType,
    setEquitySource,
    setSearchQuery,
  } = useAddAssetState(mappedAssetType);
  const [isLoading, setIsLoading] = useState(false);
  const [currentlyAllocatedAssetIds, setCurrentlyAllocatedAssetIds] = useState<Set<string>>(new Set());
  const [totalRecordCount, setTotalRecordCount] = useState<number>(0);
  const [isLoadingCount, setIsLoadingCount] = useState(false);

  // Fetch assets based on selected type and equity source when applicable
  const fetchAssets = useCallback(async () => {
    if (!open) return;
    
    setIsLoading(true);
    logger.debug('useEnhancedAddAsset', 'Fetching assets', { selectedAssetType, equitySource, searchQuery });
    
    try {
      const results = await fetchAssetsFor({ selectedAssetType, equitySource, searchQuery });
      setAssets(results || []);
    } catch (error) {
      logger.error('useEnhancedAddAsset', 'Error fetching assets', { error });
      toast.error("Failed to load assets");
    } finally {
      setIsLoading(false);
    }
  }, [open, selectedAssetType, equitySource, searchQuery]);
  
  // Fetch record count for current asset type and source
  const fetchRecordCount = useCallback(async () => {
    if (!open) return;
    
    setIsLoadingCount(true);
    logger.debug('useEnhancedAddAsset', 'Fetching record count', { selectedAssetType, equitySource });
    
    try {
      const count = await getRecordCount(selectedAssetType, equitySource);
      logger.info('useEnhancedAddAsset', 'Record count fetch completed', { count });
      setTotalRecordCount(count || 0);
    } catch (error) {
      logger.error('useEnhancedAddAsset', 'Error fetching record count', { error });
      setTotalRecordCount(0);
    } finally {
      setIsLoadingCount(false);
    }
  }, [open, selectedAssetType, equitySource]);
  
  // Fetch current allocations to disable already allocated assets
  const fetchCurrentAllocations = async () => {
    if (!portfolioId) return;
    try {
      const set = await fetchAllocatedAssetIdSet(portfolioId);
      setCurrentlyAllocatedAssetIds(set);
    } catch (error) {
      logger.error('useEnhancedAddAsset', 'Error fetching current allocations', { error });
      toast.error("Failed to load current allocations");
    }
  };
  
  // Handle asset type selection change
  const handleAssetTypeChange = (type: AssetType) => {
    setSelectedAssetType(type);
    setAssets([]);
    setSearchQuery("");
    setTotalRecordCount(0);
    if (type === 'equity') setEquitySource('stock');
  };
  
  // Handle equity source change
  const handleEquitySourceChange = (source: 'stock' | 'fund') => {
    setEquitySource(source);
    setAssets([]);
    setTotalRecordCount(0);
  };
  
  // Handle search query changes
  const handleSearchQueryChange = (query: string) => setSearchQuery(query);

  // Effect to fetch assets and record count when parameters change (debounced), centralized
  useAddAssetData({
    open,
    selectedAssetType,
    equitySource,
    searchQuery,
    fetchRecordCount,
    fetchAssets,
  });

  // Handle form submission - this is the main submit handler
  const handleSubmit = async (values: { assetId: string }) => {
    await insertPortfolioAllocation(values.assetId);
  };
  
  // Insert a new portfolio allocation (delegates to utility)
  const insertPortfolioAllocation = async (assetId: string) => {
    setIsLoading(true);
    try {
      const selectedAsset = assets.find(asset => asset.id === assetId);
      if (!selectedAsset) throw new Error('Selected asset not found');

      logger.info('useEnhancedAddAsset', 'Saving asset to portfolio', { source: selectedAsset.source, assetName: selectedAsset.name });
      await performAllocationInsert({ portfolioId, selectedAsset });

      // Success: trigger callbacks and close dialog
      onAssetAdded();
      if (externalSetOpen) externalSetOpen(false); else handleOpenChange(false);
    } catch (err) {
      logger.error('useEnhancedAddAsset', 'Error adding asset to portfolio', { err });
      toast.error(err instanceof Error ? err.message : 'An error occurred adding the asset');
    } finally {
      setIsLoading(false);
    }
  };

  // Removed duplicate debounce effect (now handled by useAddAssetData)
  
  // Fetch current allocations when dialog opens
  useEffect(() => {
    if (open) {
      fetchCurrentAllocations();
    }
  }, [open, portfolioId]);

  // Handle open state changes
  const handleOpenChange = (newOpen: boolean) => {
    // Clear assets and reset search when dialog state changes
    if (newOpen) {
      // Reset state when opening
      setAssets([]);
      setSearchQuery("");
      // Note: allocated assets are already fetched in a separate useEffect 
      // when the dialog opens via the dependency on [open]
    } else {
      // Clear state when closing
      setAssets([]);
      setSearchQuery("");
    }
    
    // Only change state if using internal state management
    if (externalOpen === undefined) {
      setInternalOpen(newOpen);
    }
    // If using external state, this function essentially becomes a no-op
  };

  return {
    open,
    setOpen: handleOpenChange,
    assets,
    isLoading,
    currentlyAllocatedAssetIds,
    selectedAssetType,
    equitySource,
    searchQuery,
    totalRecordCount,
    isLoadingCount,
    handleAssetTypeChange,
    handleEquitySourceChange,
    handleSearchQueryChange,
    handleSubmit
  };
}
